/**
 * LiveClassesUserPage — user-facing home for Live Classes with two sections:
 *   1. Upcoming — scheduled + live classes with "Ask & Vote" and "Join" CTAs
 *   2. Past classes — published class archives (video + summary + Q&A) with search
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { usePreferences } from '../context/PreferencesContext';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Video, Calendar, Clock, Users, Loader2, Radio, ArrowRight, AlertCircle,
  MessagesSquare, Search, PlayCircle, Archive, User as UserIcon, Ban, CalendarClock,
  Lock, ArrowRightLeft, UserCog, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Nautical grades don't have MMD-specific exams — MMD only applies to
// engineering grades. Kept in sync with backend/routes/bulk_import.py.
const NAUTICAL_GRADES = ['Master', 'Chief Mate', '2MFG'];
const isNauticalGrade = (grade) => NAUTICAL_GRADES.includes(grade);

const fmtIST = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short',
    });
  } catch { return iso; }
};

const fmtDateOnly = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata', dateStyle: 'medium',
    });
  } catch { return iso; }
};

// Returns { canJoin, label, isSoon } based on current time and class start
const computeJoinState = (cls) => {
  const now = Date.now();
  const start = new Date(cls.start_time).getTime();
  const end = start + (cls.duration_minutes + 15) * 60 * 1000;
  const openAt = start - 15 * 60 * 1000; // Join opens 15 min before start
  if (cls.status === 'live') return { canJoin: true, label: 'Join Now', isSoon: true };
  if (now < openAt) {
    const minsAway = Math.round((openAt - now) / 60000);
    if (minsAway < 60) return { canJoin: false, label: `Opens in ${minsAway} min`, isSoon: minsAway < 30 };
    const hoursAway = Math.round((start - now) / 3600000);
    if (hoursAway < 24) return { canJoin: false, label: `In ${hoursAway} h`, isSoon: false };
    return { canJoin: false, label: fmtIST(cls.start_time), isSoon: false };
  }
  if (now > end) return { canJoin: false, label: 'Ended', isSoon: false };
  return { canJoin: true, label: 'Join Now', isSoon: true };
};

// -------------- Upcoming card --------------
const RESCHEDULE_WINDOW_MS = 48 * 60 * 60 * 1000; // show badge for 48h after reschedule

const RescheduledChip = ({ cls }) => {
  if (!cls.rescheduled_at || !cls.previous_start_time) return null;
  const ts = new Date(cls.rescheduled_at).getTime();
  if (isNaN(ts) || Date.now() - ts > RESCHEDULE_WINDOW_MS) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-300 text-[10px] font-semibold text-amber-800"
      title={`Rescheduled from ${fmtIST(cls.previous_start_time)} → ${fmtIST(cls.start_time)}`}
      data-testid={`live-class-rescheduled-chip-${cls.id}`}
    >
      <CalendarClock className="w-3 h-3" />
      Rescheduled
    </span>
  );
};

const CancelledBanner = ({ cls }) => {
  if (cls.status !== 'cancelled') return null;
  return (
    <div
      className="mb-2 rounded border border-red-200 bg-red-50 p-2.5 text-xs text-red-900 flex items-start gap-2"
      data-testid={`live-class-cancelled-banner-${cls.id}`}
    >
      <Ban className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <div>
        <p className="font-semibold">This class has been cancelled by faculty.</p>
        {cls.cancellation_reason && (
          <p className="mt-0.5 whitespace-pre-wrap opacity-90">{cls.cancellation_reason}</p>
        )}
      </div>
    </div>
  );
};

const UpcomingCard = ({ cls, navigate, fnById }) => {
  const { canJoin, label, isSoon } = computeJoinState(cls);
  const isLive = cls.status === 'live';
  const isCancelled = cls.status === 'cancelled';

  const handleJoin = () => {
    if (!canJoin || isCancelled) return;
    navigate(`/live-classes/${cls.id}/join`);
  };

  const fnChips = (cls.functions || []).map(id => fnById(id)).filter(Boolean);

  return (
    <Card
      className={`transition-shadow hover:shadow-md ${isLive ? 'border-l-4 border-l-[#FF6B6B]' : ''} ${isCancelled ? 'opacity-80' : ''}`}
      data-testid={`live-class-card-${cls.id}`}
    >
      <CardContent className="p-5">
        <CancelledBanner cls={cls} />
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {isLive && (
                <Badge className="bg-[#FF6B6B] text-white border-0 gap-1">
                  <Radio className="w-3 h-3 animate-pulse" /> Live now
                </Badge>
              )}
              {isCancelled && (
                <Badge className="bg-red-100 text-red-800 border border-red-300 gap-1">
                  <Ban className="w-3 h-3" /> Cancelled
                </Badge>
              )}
              <RescheduledChip cls={cls} />
              {Array.isArray(cls.grades) && cls.grades.map(g => (
                <Badge key={g} variant="outline" className="text-xs">{g}</Badge>
              ))}
              {Array.isArray(cls.mmd_locations) && cls.mmd_locations.map(m => (
                <Badge key={m} variant="outline" className="text-xs bg-slate-50 text-slate-700 border-slate-300" data-testid={`user-cls-mmd-${m}`}>
                  {m}
                </Badge>
              ))}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1 break-words">{cls.title}</h3>
            {cls.description && (
              <p className="text-sm text-gray-600 mb-2 line-clamp-2 break-words">{cls.description}</p>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
              <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{fmtIST(cls.start_time)}</span>
              <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{cls.duration_minutes} min</span>
              <span className="flex items-center gap-1.5"><UserIcon className="w-3.5 h-3.5" />{cls.host_user_name}</span>
            </div>
            {fnChips.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {fnChips.map(fn => (
                  <span
                    key={fn.id}
                    className={`px-1.5 py-0.5 rounded text-[10px] font-medium border max-w-full whitespace-normal break-words leading-snug text-left ${fn.exam_type === 'orals'
                      ? 'bg-[#0A7EA4]/8 text-[#0A7EA4] border-[#0A7EA4]/25'
                      : 'bg-purple-50 text-purple-700 border-purple-200'}`}
                    title={`${fn.name} · ${(fn.exam_type || 'general').toUpperCase()}`}
                  >
                    <span className="opacity-60 mr-0.5">F{fn.function_number}</span>
                    {fn.name}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col items-stretch gap-2 min-w-[180px]">
            <Button
              onClick={handleJoin}
              disabled={!canJoin || isCancelled}
              className={`h-10 font-semibold ${isCancelled
                ? 'bg-gray-200 text-gray-500'
                : canJoin
                  ? 'bg-[#FF6B6B] hover:bg-[#FF5555] text-white shadow-sm'
                  : (isSoon ? 'bg-amber-500 text-white' : 'bg-gray-300 text-gray-600')}`}
              data-testid={`live-class-join-btn-${cls.id}`}
            >
              {isCancelled ? (
                <><Ban className="w-4 h-4 mr-2" />Cancelled</>
              ) : canJoin ? (
                <>
                  <Video className="w-4 h-4 mr-2" />
                  {label}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              ) : (
                <>
                  <Clock className="w-4 h-4 mr-2" />
                  {label}
                </>
              )}
            </Button>
            <Button
              onClick={() => navigate(`/live-classes/${cls.id}/questions`, { state: { from: '/live-classes' } })}
              className="h-10 font-semibold bg-[#0A7EA4]/10 hover:bg-[#0A7EA4]/20 text-[#0A7EA4] border border-[#0A7EA4]/25 shadow-none"
              data-testid={`live-class-qna-btn-${cls.id}`}
            >
              <MessagesSquare className="w-4 h-4 mr-2" />
              Ask &amp; Vote
              {typeof cls.qna_count === 'number' && cls.qna_count > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#0A7EA4] text-white text-[11px] font-semibold tabular-nums">
                  {cls.qna_count}
                </span>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// -------------- Function filter block (reusable for Upcoming + Past) --------------
const FunctionFilterBlock = ({ groupedFunctions, selected, onToggle, onClear, fnById, sectionKey }) => {
  const totalCatalog =
    groupedFunctions.orals.length + groupedFunctions.written.length + groupedFunctions.other.length;
  if (totalCatalog === 0) return null;

  const selectedNames = selected.map(id => fnById(id)?.name).filter(Boolean);

  return (
    <div className="mb-4 p-3 rounded-lg border bg-gray-50/60" data-testid={`${sectionKey}-function-filter`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Filter by function</p>
          {/* Colour legend so students know what teal/purple mean */}
          <div className="flex items-center gap-2 text-[10px] text-gray-500">
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full bg-[#0A7EA4] border border-[#0A7EA4]" />
              Orals
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full bg-purple-600 border border-purple-600" />
              Written
            </span>
          </div>
        </div>
        {selected.length > 0 && (
          <button
            onClick={onClear}
            className="text-xs text-[#0A7EA4] hover:underline"
            data-testid={`${sectionKey}-function-filter-clear`}
          >
            Clear ({selected.length})
          </button>
        )}
      </div>

      {[
        { key: 'orals', label: 'Orals', items: groupedFunctions.orals },
        { key: 'written', label: 'Written', items: groupedFunctions.written },
        { key: 'other', label: 'Other', items: groupedFunctions.other },
      ].filter(g => g.items.length > 0).map(group => (
        <div key={group.key} className="mb-2 last:mb-0" data-testid={`${sectionKey}-function-group-${group.key}`}>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
            {group.label}
            <span className="ml-1 text-gray-400 font-normal">({group.items.length})</span>
          </p>
          <div className="flex flex-wrap gap-1.5">
            {group.items.map(fn => {
              const active = selected.includes(fn.id);
              const activeCls = group.key === 'orals'
                ? 'bg-[#0A7EA4] text-white border-[#0A7EA4]'
                : group.key === 'written'
                  ? 'bg-purple-600 text-white border-purple-600'
                  : 'bg-gray-600 text-white border-gray-600';
              const hoverCls = group.key === 'orals'
                ? 'hover:border-[#0A7EA4] hover:text-[#0A7EA4]'
                : group.key === 'written'
                  ? 'hover:border-purple-500 hover:text-purple-700'
                  : 'hover:border-gray-500 hover:text-gray-800';
              return (
                <button
                  key={fn.id}
                  onClick={() => onToggle(fn.id)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${active
                    ? activeCls
                    : `bg-white text-gray-700 border-gray-300 ${hoverCls}`}`}
                  data-testid={`${sectionKey}-function-chip-${fn.id}`}
                  title={fn.name}
                >
                  <span className="opacity-70 mr-1">F{fn.function_number}</span>
                  {fn.name}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {selected.length > 0 && (
        <p className="text-[11px] text-gray-600 mt-2 pt-2 border-t border-gray-200" data-testid={`${sectionKey}-function-filter-summary`}>
          Showing classes tagged: <span className="font-semibold text-gray-800">{selectedNames.join(', ')}</span>
        </p>
      )}
    </div>
  );
};

const LiveClassesUserPage = () => {
  const navigate = useNavigate();
  const { preferences, updatePreferences } = usePreferences();
  const userGrade = preferences?.grade;
  const userMmd = preferences?.mmd_location;
  // MMD is mandatory ONLY for engineering grades (nautical grades don't have
  // MMD-specific exams). Matches the pattern in QuestionBankPage.
  const needsMmd = userGrade && !isNauticalGrade(userGrade) && !userMmd;

  // ── Module activation gate ───────────────────────────────────────────
  // Mirrors the pattern used across Notebook, Insights, etc. Four states:
  //   • hidden      → module disabled globally OR for this user's grade;
  //                   redirect home so no ghost route exists in the SPA.
  //   • coming_soon → admin toggled it publicly-visible-but-not-yet-live
  //                   for this grade; render splash so users know it's on
  //                   the roadmap.
  //   • locked      → module active for this grade, but the user's plan
  //                   doesn't include live_classes; render splash with a
  //                   direct upgrade CTA to /plans?highlight=live_classes.
  //   • active      → normal page content (original behavior below).
  //
  // Live Classes rolls out grade-by-grade as instructor capacity comes
  // online — we resolve status against `live_classes_<grade>` in
  // module_settings. If the user hasn't set a grade yet, we fall back to
  // the parent `live_classes` row's status. Missing rows (null) are
  // treated as "active" for backward compatibility.
  const { canAccessLiveClasses, isPaid, subscribedGrade } = useSubscriptionAccess();
  const { getLiveClassesStatusForGrade, getModuleStatus } = useModuleAccess();
  const gradeSpecificStatus = userGrade && getLiveClassesStatusForGrade
    ? getLiveClassesStatusForGrade(userGrade)
    : null;
  const parentStatus = getModuleStatus ? getModuleStatus('live_classes') : null;
  const moduleStatus = gradeSpecificStatus ?? parentStatus;

  // Handle "hidden" — redirect home. Rendering a splash for a hidden module
  // would defeat the purpose of hiding it.
  useEffect(() => {
    if (moduleStatus === 'hidden') {
      navigate('/', { replace: true });
    }
  }, [moduleStatus, navigate]);

  const isComingSoon = moduleStatus === 'coming_soon';
  const isLocked = !isComingSoon && !canAccessLiveClasses();

  // Paid user in the wrong scope — same states we render on Notebook /
  // UpgradeModal. Only fires when isLocked is already true.
  const isGradeMismatch =
    isLocked && isPaid && subscribedGrade && subscribedGrade !== 'all' &&
    userGrade && subscribedGrade !== userGrade;
  const isGradeUnset = isLocked && isPaid && !userGrade && !subscribedGrade;

  const [switchingGrade, setSwitchingGrade] = useState(false);
  const handleSwitchGrade = async () => {
    if (!subscribedGrade) return;
    setSwitchingGrade(true);
    try {
      await updatePreferences({ grade: subscribedGrade });
      toast.success(`Switched to ${subscribedGrade}`);
      window.location.reload();
    } catch (err) {
      toast.error('Could not switch grade. Please try from Profile → Preferences.');
    } finally {
      setSwitchingGrade(false);
    }
  };

  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [loadingUpcoming, setLoadingUpcoming] = useState(true);
  const [loadingPast, setLoadingPast] = useState(true);
  const [error, setError] = useState('');

  // Function filter — Upcoming section only. The archive lives on its own
  // page (/live-classes/archive) with its own filter state.
  const [functionCatalog, setFunctionCatalog] = useState([]);
  const [selectedUpcomingFunctions, setSelectedUpcomingFunctions] = useState([]);
  const toggleUpcomingFunction = (id) => setSelectedUpcomingFunctions(v =>
    v.includes(id) ? v.filter(x => x !== id) : [...v, id]
  );

  // Load the user's grade-specific function catalog once we know the grade.
  // Only functions attached to the user's grade are relevant as a filter.
  useEffect(() => {
    if (!userGrade) { setFunctionCatalog([]); return; }
    axios.get(`${API}/functions`, { params: { grade: userGrade } })
      .then(({ data }) => setFunctionCatalog(Array.isArray(data) ? data : []))
      .catch(() => setFunctionCatalog([]));
  }, [userGrade]);

  const loadUpcoming = useCallback(async () => {
    try {
      setLoadingUpcoming(true);
      const { data } = await axios.get(`${API}/live-classes/upcoming`);
      setUpcoming(data || []);
    } catch (err) {
      if (err.response?.status === 403) {
        // State-aware message: mirrors what the top-of-page locked card
        // shows so users get consistent copy whether they hit the render
        // gate or the API 403 fallback.
        if (isPaid && subscribedGrade && subscribedGrade !== 'all' && userGrade && subscribedGrade !== userGrade) {
          setError(`Your active subscription is for ${subscribedGrade}. Live Classes here are scoped to ${userGrade} — switch your grade back or purchase a plan for ${userGrade} as well.`);
        } else if (isPaid && !userGrade && !subscribedGrade) {
          setError('Please pick a grade in Profile → Preferences to unlock Live Classes.');
        } else {
          setError('Live Classes are not included in your current plan. Please upgrade to access this feature.');
        }
      } else {
        setError(err.response?.data?.detail || 'Failed to load live classes');
      }
    } finally {
      setLoadingUpcoming(false);
    }
  }, [isPaid, subscribedGrade, userGrade]);

  const loadPast = useCallback(async () => {
    try {
      setLoadingPast(true);
      const { data } = await axios.get(`${API}/live-classes/past`);
      setPast(data || []);
    } catch (err) {
      // Silent — the CTA to the archive page still renders even if the
      // count fails to load
      if (err.response?.status !== 403) {
        console.warn('Failed to load archive count:', err);
      }
    } finally {
      setLoadingPast(false);
    }
  }, []);

  // Skip the load API calls when the user can't view classes anyway — no
  // point in generating a 403 that we'd then swallow to render a splash.
  useEffect(() => {
    if (isLocked || isComingSoon || moduleStatus === 'hidden') return;
    loadUpcoming();
  }, [loadUpcoming, isLocked, isComingSoon, moduleStatus]);
  useEffect(() => {
    if (isLocked || isComingSoon || moduleStatus === 'hidden') return;
    loadPast();
  }, [loadPast, isLocked, isComingSoon, moduleStatus]);

  const upcomingCount = upcoming.length;
  const pastCount = past.length;

  // Quick id → function lookup for card chip hydration
  const fnById = useCallback(
    (id) => functionCatalog.find(fn => fn.id === id) || null,
    [functionCatalog]
  );

  // Split the function catalog by exam_type for the filter block.
  const groupedFunctions = useMemo(() => {
    const orals = [];
    const written = [];
    const other = [];
    for (const fn of functionCatalog) {
      const t = (fn.exam_type || '').toLowerCase();
      if (t === 'orals' || t === 'oral') orals.push(fn);
      else if (t === 'written') written.push(fn);
      else other.push(fn);
    }
    return { orals, written, other };
  }, [functionCatalog]);

  // Client-side function filter for Upcoming (union match)
  const filteredUpcoming = useMemo(() => {
    if (selectedUpcomingFunctions.length === 0) return upcoming;
    const wanted = new Set(selectedUpcomingFunctions);
    return upcoming.filter(cls =>
      Array.isArray(cls.functions) && cls.functions.some(fid => wanted.has(fid))
    );
  }, [upcoming, selectedUpcomingFunctions]);

  // Full-page splash for Coming Soon / Locked / Hidden states — matches
  // the dashboard locked-card copy so users get consistent messaging no
  // matter where they enter from.
  if (moduleStatus === 'hidden') {
    // The useEffect above already redirects; render a spinner while it
    // fires so the UI doesn't flash the "locked" splash in between.
    return (
      <Layout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </Layout>
    );
  }

  if (isComingSoon || isLocked) {
    return (
      <Layout>
        <div
          className="max-w-3xl mx-auto py-12 px-4"
          data-testid={isComingSoon ? 'live-classes-coming-soon-splash' : 'live-classes-locked-splash'}
        >
          <Card className="border-t-4 border-t-[#FF6B6B] shadow-lg">
            <CardContent className="p-8 sm:p-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-[#FF6B6B]/10 rounded-xl">
                  <Video className="w-8 h-8 text-[#FF6B6B]" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Live Classes</h1>
                  {isComingSoon ? (
                    <Badge className="mt-1 bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-100 gap-1">
                      <Clock className="w-3 h-3" />
                      Coming Soon
                    </Badge>
                  ) : (
                    <Badge className="mt-1 bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-100 gap-1">
                      <Lock className="w-3 h-3" />
                      Premium
                    </Badge>
                  )}
                </div>
              </div>

              <p className="text-gray-600 mb-6">
                {isComingSoon
                  ? 'Instructor-led live sessions are launching soon. Once available, you\u2019ll be able to ask questions in advance, join live, and revisit the archive.'
                  : isGradeMismatch
                    ? <>Your active subscription is for <span className="font-semibold text-gray-900">{subscribedGrade}</span>, but Live Classes here are scoped to <span className="font-semibold text-gray-900">{userGrade}</span>. Switch back to the grade your plan covers, or purchase a plan for {userGrade} as well.</>
                    : isGradeUnset
                      ? 'Please pick a grade in Profile → Preferences to unlock instructor-led Live Classes for your syllabus.'
                      : 'Instructor-led live sessions with real-time Q\u0026A aren\u2019t included in your current plan. Upgrade to join weekly sessions with experienced hosts.'}
              </p>

              <ul className="space-y-3 text-sm text-gray-700 mb-8 border-l-2 border-[#FF6B6B]/30 pl-4">
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-[#FF6B6B] rounded-full mt-2 flex-shrink-0" />
                  <span>Weekly live discussions on tough topics from your syllabus</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-[#FF6B6B] rounded-full mt-2 flex-shrink-0" />
                  <span>Grade-specific sessions hosted by experienced surveyors and instructors</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-[#FF6B6B] rounded-full mt-2 flex-shrink-0" />
                  <span>Pre-class Q&amp;A polling &mdash; upvote the questions you want answered</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-[#FF6B6B] rounded-full mt-2 flex-shrink-0" />
                  <span>Join right from your browser &mdash; no downloads required</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-[#FF6B6B] rounded-full mt-2 flex-shrink-0" />
                  <span>Searchable archive of every published class</span>
                </li>
              </ul>

              {/* CTA row — three variants for the three locked states.
                  Copy stays scoped to Live Classes; only the actions change. */}
              {isLocked && isGradeMismatch && (
                <>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={handleSwitchGrade}
                      disabled={switchingGrade}
                      className="bg-[#0A7EA4] hover:bg-[#08678A] text-white gap-2 h-11 px-6"
                      data-testid="live-classes-switch-grade-btn"
                    >
                      {switchingGrade ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRightLeft className="w-4 h-4" />}
                      Switch to {subscribedGrade}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/plans?grade=${encodeURIComponent(userGrade)}`)}
                      disabled={switchingGrade}
                      className="gap-2 h-11 px-6"
                      data-testid="live-classes-buy-other-grade-btn"
                    >
                      <Sparkles className="w-4 h-4" />
                      Buy plan for {userGrade}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    Switching keeps your existing plan. You can switch back anytime from Profile → Preferences.
                  </p>
                </>
              )}
              {isLocked && !isGradeMismatch && isGradeUnset && (
                <>
                  <Button
                    onClick={() => navigate('/profile')}
                    className="w-full sm:w-auto bg-[#0A7EA4] hover:bg-[#08678A] text-white gap-2 h-11 px-6"
                    data-testid="live-classes-open-profile-btn"
                  >
                    <UserCog className="w-4 h-4" />
                    Open Profile
                  </Button>
                </>
              )}
              {isLocked && !isGradeMismatch && !isGradeUnset && (
                <>
                  <Button
                    onClick={() => navigate('/plans?highlight=live_classes')}
                    className="w-full sm:w-auto bg-[#FF6B6B] hover:bg-[#FF5555] text-white gap-2 h-11 px-6"
                    data-testid="live-classes-upgrade-btn"
                  >
                    <Lock className="w-4 h-4" />
                    Upgrade to Access
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                  <p className="text-xs text-gray-500 mt-3">
                    Included in plans marked with the &ldquo;Live Classes&rdquo; badge on the plans page.
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-transition max-w-5xl mx-auto" data-testid="live-classes-user-page">
        {/* ---------- Header (matches platform pattern: text-3xl font-bold text-gray-900) ---------- */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="live-classes-page-heading">
                Live Classes
              </h1>
              <p className="text-sm text-gray-600">
                {userGrade ? (
                  <>Instructor-led sessions for <span className="font-semibold text-gray-900">{userGrade}</span>. Ask questions in advance, join live, and browse the archive.</>
                ) : (
                  <>Join instructor-led sessions, ask questions in advance, and browse past class archives.</>
                )}
              </p>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <div className="text-right">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Upcoming</p>
                <p className="text-2xl font-bold text-[#0A7EA4] tabular-nums" data-testid="upcoming-count">{upcomingCount}</p>
              </div>
              <div className="h-10 w-px bg-gray-200" />
              <div className="text-right">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Archived</p>
                <p className="text-2xl font-bold text-emerald-700 tabular-nums" data-testid="archive-count">{pastCount}</p>
              </div>
            </div>
          </div>
        </div>

        {error ? (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-6 flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-amber-900 mb-1">Access restricted</p>
                <p className="text-sm text-amber-800">{error}</p>
                <Button onClick={() => navigate('/plans')} className="mt-3" size="sm" data-testid="live-classes-upgrade-btn">
                  View Plans
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (!userGrade || needsMmd) ? (
          <Card className="border-[#0A7EA4]/25 bg-[#0A7EA4]/5" data-testid="live-classes-no-prefs">
            <CardContent className="p-6 flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-[#0A7EA4] flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900 mb-1">
                  {!userGrade
                    ? 'Pick your grade to see live classes'
                    : 'Set your MMD to see live classes'}
                </p>
                <p className="text-sm text-gray-700">
                  {!userGrade
                    ? 'Live classes are tailored to your certification level. Set your grade in preferences to unlock the sessions relevant to you.'
                    : 'Some live classes are targeted to specific MMD locations. Set your MMD in preferences so you don\u2019t miss location-specific sessions.'}
                </p>
                <Button
                  onClick={() => navigate('/profile')}
                  className="mt-3 bg-[#0A7EA4] hover:bg-[#086684]"
                  size="sm"
                  data-testid="live-classes-set-prefs-btn"
                >
                  {!userGrade ? 'Set your grade' : 'Set your MMD'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* -------- Upcoming section -------- */}
            <section className="mb-10" data-testid="upcoming-section">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1 h-6 bg-[#FF6B6B] rounded-full" />
                <h2 className="text-lg font-semibold text-gray-900">Upcoming</h2>
                <span className="text-xs text-gray-500 tabular-nums">
                  ({filteredUpcoming.length}{selectedUpcomingFunctions.length > 0 ? ` of ${upcomingCount}` : ''})
                </span>
              </div>

              {/* Function filter — mirror of the Past block, resets on refresh */}
              <FunctionFilterBlock
                groupedFunctions={groupedFunctions}
                selected={selectedUpcomingFunctions}
                onToggle={toggleUpcomingFunction}
                onClear={() => setSelectedUpcomingFunctions([])}
                fnById={fnById}
                sectionKey="upcoming"
              />

              {loadingUpcoming ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : filteredUpcoming.length === 0 ? (
                <Card>
                  <CardContent className="p-10 text-center text-gray-500" data-testid="live-classes-user-empty">
                    <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <h3 className="text-base font-semibold text-gray-700 mb-1">
                      {selectedUpcomingFunctions.length > 0
                        ? 'No upcoming classes match the selected function(s)'
                        : 'No classes scheduled yet'}
                    </h3>
                    <p className="text-sm">
                      {selectedUpcomingFunctions.length > 0
                        ? 'Try removing a function filter or clear all.'
                        : 'Check back soon — new sessions are added regularly.'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-3">
                  {filteredUpcoming.map(cls => (
                    <UpcomingCard key={cls.id} cls={cls} navigate={navigate} fnById={fnById} />
                  ))}
                </div>
              )}
            </section>

            {/* -------- Class archive CTA (moved to /live-classes/archive) -------- */}
            <section className="mt-6" data-testid="past-cta-section">
              <Card
                className="border-2 border-dashed border-emerald-300 bg-emerald-50/50 cursor-pointer hover:bg-emerald-50 hover:border-emerald-400 transition-colors group"
                onClick={() => navigate('/live-classes/archive')}
                data-testid="browse-archive-cta"
              >
                <CardContent className="p-5">
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 group-hover:bg-emerald-200 transition-colors">
                        <Archive className="w-5 h-5 text-emerald-700" />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">Browse the Class Archive</h3>
                        <p className="text-sm text-gray-600">
                          Recordings, summaries and Q&amp;A of every past class in your grade.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-emerald-700 font-medium text-sm">
                      Open archive <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>
          </>
        )}
      </div>
    </Layout>
  );
};

export default LiveClassesUserPage;
